/**
 * useSettings.ts
 *
 * Per-user preferences, stored in Office roaming settings (see
 * RoamingSettingsLike in office/officeShim.ts for why not localStorage).
 *
 * Scope filters live here rather than on the server on purpose. There is one
 * shared project index built by one nightly crawl, so "where do I search" is
 * not something a user can change about the *index* — that's an admin action
 * against `/api/v1/admin/settings`. What a user can change is their own view
 * over it, which is just three query params on /projects/search. Keeping that
 * client-side means no per-user table, no migration when Egnyte OAuth replaces
 * the shared dev identity, and no server round-trip to read a preference.
 */
import { useCallback, useState } from "react";

import type { OfficeCtx } from "../office/officeShim";
import type { ItemType } from "../api/types";

export interface UserSettings {
  /** Division buckets to search. Empty = all. */
  divisions: string[];
  /** Two-digit year buckets to search. Empty = all. */
  years: string[];
  /** Restrict results to projects under this folder. Null = the whole index. */
  pathPrefix: string | null;
  /** What the item-type selector starts on for a newly opened email. */
  defaultItemType: ItemType;
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  divisions: [],
  years: [],
  pathPrefix: null,
  defaultItemType: "email",
};

const ROAMING_KEY = "filer.userSettings";
const ITEM_TYPES: ItemType[] = ["email", "rfi", "submittal"];

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Rebuild a UserSettings from whatever the roaming store actually returned.
 *
 * Defensive on purpose: this value can predate the current shape (written by an
 * older build of the add-in, and roaming settings outlive any single version),
 * so every field is validated rather than trusted. A malformed blob degrades to
 * the defaults instead of throwing during render.
 */
export function coerceUserSettings(raw: unknown): UserSettings {
  if (typeof raw !== "object" || raw === null) {
    return DEFAULT_USER_SETTINGS;
  }
  const source = raw as Partial<Record<keyof UserSettings, unknown>>;
  const prefix =
    typeof source.pathPrefix === "string" && source.pathPrefix.trim()
      ? source.pathPrefix.trim()
      : null;
  const itemType = ITEM_TYPES.includes(source.defaultItemType as ItemType)
    ? (source.defaultItemType as ItemType)
    : DEFAULT_USER_SETTINGS.defaultItemType;

  return {
    divisions: toStringArray(source.divisions),
    years: toStringArray(source.years),
    pathPrefix: prefix,
    defaultItemType: itemType,
  };
}

export interface UseUserSettingsResult {
  settings: UserSettings;
  update: (patch: Partial<UserSettings>) => Promise<void>;
  isSaving: boolean;
  saveError: string | null;
}

export function useUserSettings(officeCtx: OfficeCtx): UseUserSettingsResult {
  const [settings, setSettings] = useState<UserSettings>(() =>
    coerceUserSettings(officeCtx.roamingSettings.get(ROAMING_KEY)),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const update = useCallback(
    async (patch: Partial<UserSettings>): Promise<void> => {
      // Applied locally first so the control responds immediately; saveAsync
      // round-trips to the Exchange mailbox and is slow enough to feel laggy.
      const next = { ...settings, ...patch };
      setSettings(next);
      setIsSaving(true);
      try {
        officeCtx.roamingSettings.set(ROAMING_KEY, next);
        await officeCtx.roamingSettings.save();
        setSaveError(null);
      } catch (err) {
        setSaveError(
          err instanceof Error ? err.message : "Could not save your settings",
        );
      } finally {
        setIsSaving(false);
      }
    },
    [settings, officeCtx],
  );

  return { settings, update, isSaving, saveError };
}
